import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { CrmIntegrationService } from 'src/app/private/services/crm-integration.service';
import {
  INTEGRATION_CATEGORY_MODULES,
  INTEGRATION_PERMISSION_PARENTS,
} from 'src/app/private/services/module.config';
import { PermissionService } from 'src/app/private/services/permission.service';
import {
  ALL_INTEGRATIONS,
  CRM_LEAD_PROVIDERS,
  CRM_SYNC_META,
  INTEGRATION_CATEGORIES,
  IntegrationCategory,
  IntegrationItem,
  CategoryTab,
} from './integrations.data';

const STORAGE_KEY = 'gravitas_integrations_state';
const CRM_PROVIDER_IDS = new Set<string>(CRM_LEAD_PROVIDERS);
const VALID_INTEGRATION_CATEGORIES = new Set<string>([
  'all',
  ...INTEGRATION_CATEGORIES.filter((c) => c.id !== 'all').map((c) => c.id),
]);

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
})
export class IntegrationsComponent implements OnInit, OnDestroy {
  categories: CategoryTab[] = INTEGRATION_CATEGORIES;
  selectedCategory: 'all' | IntegrationCategory = 'all';
  searchQuery = '';
  selectedStatus: 'all' | 'connected' | 'available' | 'native' = 'all';
  private destroy$ = new Subject<void>();

  integrations: IntegrationItem[] = [];
  selectedIntegration: IntegrationItem | null = null;
  configModalOpen = false;
  syncModalOpen = false;
  syncTarget: IntegrationItem | null = null;
  syncLeads = true;
  syncContacts = true;
  syncResult: { success: boolean; message: string } | null = null;

  formData: Record<string, string> = {};
  testingConnection = false;
  savingConfig = false;
  importingLeads = false;
  testResult: { success: boolean; message: string } | null = null;
  copiedWebhook = false;

  constructor(
    private toast: ToastrService,
    private localStorage: LocalStorageService,
    private crmApi: CrmIntegrationService,
    private route: ActivatedRoute,
    private router: Router,
    private permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.loadState();
    this.loadCrmConnections();
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const status = params['status'];
      if (
        status === 'connected' ||
        status === 'available' ||
        status === 'all' ||
        status === 'native'
      ) {
        this.selectedStatus = status;
      }

      const category = params['category'];
      const requested =
        category && VALID_INTEGRATION_CATEGORIES.has(category)
          ? (category as 'all' | IntegrationCategory)
          : 'all';

      if (!this.categoryAllowed(requested)) {
        const fallback = this.visibleCategories[0];
        if (!fallback) {
          void this.router.navigate(['/unauthorized']);
          return;
        }
        this.selectedCategory = fallback.id;
        if (requested !== fallback.id) {
          void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { category: fallback.id },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
        return;
      }

      this.selectedCategory = requested;
    });
  }

  categoryAllowed(category: string): boolean {
    const moduleKey = INTEGRATION_CATEGORY_MODULES[category];
    if (!moduleKey) return false;
    return this.permissionService.hasNavAccess(
      moduleKey,
      INTEGRATION_PERMISSION_PARENTS,
      true,
    );
  }

  get visibleCategories(): CategoryTab[] {
    return this.categories.filter((cat) => this.categoryAllowed(cat.id));
  }

  private get accessibleIntegrations(): IntegrationItem[] {
    if (this.categoryAllowed('all')) return this.integrations;
    return this.integrations.filter((item) => this.categoryAllowed(item.category));
  }

  isCrmProvider(item: IntegrationItem | null | undefined): boolean {
    return !!item && CRM_PROVIDER_IDS.has(item.id);
  }

  loadState(): void {
    const base = JSON.parse(JSON.stringify(ALL_INTEGRATIONS)) as IntegrationItem[];
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const savedState: Record<string, { status: 'connected' | 'available' | 'native'; config?: any }> =
          JSON.parse(saved);
        for (const item of base) {
          if (this.isCrmProvider(item)) continue;
          if (savedState[item.id]) {
            item.status = savedState[item.id].status;
          }
        }
      }
    } catch (e) {
      console.warn('Could not load integrations state from localStorage:', e);
    }
    this.integrations = base;
  }

  saveState(): void {
    try {
      const stateMap: Record<string, { status: 'connected' | 'available' | 'native' }> = {};
      for (const item of this.integrations) {
        if (this.isCrmProvider(item)) continue;
        stateMap[item.id] = { status: item.status };
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateMap));
    } catch (e) {
      console.warn('Could not persist integrations state:', e);
    }
  }

  loadCrmConnections(): void {
    const clientId = this.localStorage.getClientId();
    if (!clientId) return;

    this.crmApi.getCrmIntegrations(clientId).subscribe({
      next: (rows) => {
        for (const row of rows || []) {
          const item = this.integrations.find((i) => i.id === row.provider);
          if (!item) continue;
          if (row.status === 'connected' || row.status === 'available') {
            item.status = row.status;
          }
          item.last_synced_at = row.last_synced_at || null;
          item.last_sync_count = row.last_sync_count ?? null;
          item.last_sync_leads = row.last_sync_leads ?? null;
          item.last_sync_contacts = row.last_sync_contacts ?? null;
        }
      },
      error: () => {
        // Catalog still works; connection state stays at catalog defaults.
      },
    });
  }

  get filteredIntegrations(): IntegrationItem[] {
    return this.accessibleIntegrations.filter((item) => {
      const matchCategory =
        this.selectedCategory === 'all' || item.category === this.selectedCategory;
      const matchStatus =
        this.selectedStatus === 'all' || item.status === this.selectedStatus;
      const q = this.searchQuery.trim().toLowerCase();
      const matchSearch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.categoryLabel.toLowerCase().includes(q) ||
        item.features.some((f) => f.toLowerCase().includes(q));

      return matchCategory && matchStatus && matchSearch;
    });
  }

  get stats() {
    const source = this.accessibleIntegrations;
    const total = source.length;
    const connected = source.filter((i) => i.status === 'connected').length;
    const native = source.filter((i) => i.status === 'native').length;
    const available = source.filter((i) => i.status === 'available').length;
    return { total, connected, native, available };
  }

  getCategoryCount(catId: 'all' | IntegrationCategory): number {
    if (catId === 'all') return this.accessibleIntegrations.length;
    return this.accessibleIntegrations.filter((i) => i.category === catId).length;
  }

  selectCategory(catId: 'all' | IntegrationCategory): void {
    if (!this.categoryAllowed(catId)) return;
    this.selectedCategory = catId;
  }

  openConfig(item: IntegrationItem): void {
    this.selectedIntegration = item;
    this.testResult = null;
    this.formData = {};

    if (item.fields) {
      for (const f of item.fields) {
        this.formData[f.key] = f.defaultValue || '';
      }
    }

    if (!this.isCrmProvider(item)) {
      try {
        const savedConfig = localStorage.getItem(`gravitas_cfg_${item.id}`);
        if (savedConfig) {
          this.formData = { ...this.formData, ...JSON.parse(savedConfig) };
        }
      } catch (e) {}
    }

    this.configModalOpen = true;
  }

  closeConfig(): void {
    this.configModalOpen = false;
    this.selectedIntegration = null;
    this.testResult = null;
  }

  private missingRequiredFields(item: IntegrationItem): string[] {
    return (item.fields || [])
      .filter((f) => f.required && !String(this.formData[f.key] || '').trim())
      .map((f) => f.label);
  }

  private apiErrorMessage(err: any, fallback: string): string {
    return (
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      fallback
    );
  }

  testConnection(): void {
    if (!this.selectedIntegration) return;

    if (this.isCrmProvider(this.selectedIntegration)) {
      const missing = this.missingRequiredFields(this.selectedIntegration);
      if (missing.length) {
        this.testResult = { success: false, message: `Fill required fields: ${missing.join(', ')}` };
        return;
      }

      this.testingConnection = true;
      this.testResult = null;
      this.crmApi
        .testCrmIntegration(this.localStorage.getClientId(), this.selectedIntegration.id, this.formData)
        .subscribe({
          next: (res) => {
            this.testingConnection = false;
            const message = res?.message || `Connection verified with ${this.selectedIntegration?.name}`;
            this.testResult = { success: true, message };
            this.toast.success(message);
          },
          error: (err) => {
            this.testingConnection = false;
            const message = this.apiErrorMessage(err, 'Connection test failed');
            this.testResult = { success: false, message };
            this.toast.error(message);
          },
        });
      return;
    }

    this.testingConnection = true;
    this.testResult = null;
    setTimeout(() => {
      this.testingConnection = false;
      this.testResult = {
        success: true,
        message: `Successfully connected to ${this.selectedIntegration?.name} API Gateway! Webhook heartbeat active.`,
      };
      this.toast.success(`Connection verified with ${this.selectedIntegration?.name}`);
    }, 1200);
  }

  saveIntegrationConfig(): void {
    if (!this.selectedIntegration) return;

    if (this.isCrmProvider(this.selectedIntegration)) {
      const missing = this.missingRequiredFields(this.selectedIntegration);
      if (missing.length) {
        this.testResult = { success: false, message: `Fill required fields: ${missing.join(', ')}` };
        return;
      }

      this.savingConfig = true;
      this.crmApi
        .saveCrmIntegration(this.localStorage.getClientId(), this.selectedIntegration.id, this.formData)
        .subscribe({
          next: (res) => {
            this.savingConfig = false;
            if (this.selectedIntegration) {
              this.selectedIntegration.status = 'connected';
            }
            this.toast.success(res?.message || `${this.selectedIntegration?.name} connected`);
            this.closeConfig();
          },
          error: (err) => {
            this.savingConfig = false;
            const message = this.apiErrorMessage(err, 'Could not connect CRM');
            this.testResult = { success: false, message };
            this.toast.error(message);
          },
        });
      return;
    }

    this.savingConfig = true;
    try {
      localStorage.setItem(
        `gravitas_cfg_${this.selectedIntegration.id}`,
        JSON.stringify(this.formData),
      );
    } catch (e) {}

    setTimeout(() => {
      this.savingConfig = false;
      if (this.selectedIntegration && this.selectedIntegration.status !== 'native') {
        this.selectedIntegration.status = 'connected';
      }
      this.saveState();
      this.toast.success(`${this.selectedIntegration?.name} settings saved and activated!`);
      this.closeConfig();
    }, 600);
  }

  disconnectIntegration(item: IntegrationItem, event: Event): void {
    event.stopPropagation();
    if (item.status === 'native') return;
    if (!confirm(`Are you sure you want to disconnect ${item.name}?`)) return;

    if (this.isCrmProvider(item)) {
      this.crmApi.disconnectCrmIntegration(this.localStorage.getClientId(), item.id).subscribe({
        next: (res) => {
          item.status = 'available';
          item.last_synced_at = null;
          item.last_sync_count = null;
          item.last_sync_leads = null;
          item.last_sync_contacts = null;
          this.toast.info(res?.message || `${item.name} has been disconnected.`);
          if (this.selectedIntegration?.id === item.id) {
            this.closeConfig();
          }
        },
        error: (err) => {
          this.toast.error(this.apiErrorMessage(err, 'Could not disconnect CRM'));
        },
      });
      return;
    }

    item.status = 'available';
    this.saveState();
    this.toast.info(`${item.name} has been disconnected.`);
    if (this.selectedIntegration?.id === item.id) {
      this.closeConfig();
    }
  }

  crmSourceName(item: IntegrationItem | null | undefined): string {
    if (!item) return '';
    return CRM_SYNC_META[item.id]?.source || item.name;
  }

  crmLeadObject(item: IntegrationItem | null | undefined): string {
    if (!item) return 'Leads';
    return CRM_SYNC_META[item.id]?.leadObject || 'Leads';
  }

  crmContactObject(item: IntegrationItem | null | undefined): string {
    if (!item) return 'Contacts';
    return CRM_SYNC_META[item.id]?.contactObject || 'Contacts';
  }

  openSyncData(item: IntegrationItem, event?: Event): void {
    event?.stopPropagation();
    if (!this.isCrmProvider(item) || item.status !== 'connected') return;
    this.syncTarget = item;
    this.syncLeads = true;
    this.syncContacts = true;
    this.syncResult = null;
    this.syncModalOpen = true;
    this.configModalOpen = false;
  }

  closeSyncData(): void {
    this.syncModalOpen = false;
    this.syncTarget = null;
    this.syncResult = null;
  }

  confirmCrmSync(): void {
    const item = this.syncTarget;
    if (!item || this.importingLeads) return;
    if (!this.syncLeads && !this.syncContacts) {
      this.syncResult = {
        success: false,
        message: 'Select leads, contacts, or both to sync.',
      };
      return;
    }

    this.importingLeads = true;
    this.syncResult = null;
    this.crmApi
      .syncCrmIntegrationLeads(this.localStorage.getClientId(), item.id, this.localStorage.getUserId(), {
        sync_leads: this.syncLeads,
        sync_contacts: this.syncContacts,
        center_id: this.localStorage.getCenterId(),
      })
      .subscribe({
        next: (res) => {
          this.importingLeads = false;
          item.last_synced_at = res.last_synced_at || new Date().toISOString();
          item.last_sync_count = res.created ?? item.last_sync_count;
          item.last_sync_leads = res.leads_created ?? res.last_sync_leads ?? item.last_sync_leads;
          item.last_sync_contacts =
            res.contacts_created ?? res.last_sync_contacts ?? item.last_sync_contacts;
          const message = res.message || `Synced data from ${this.crmSourceName(item)}.`;
          this.syncResult = { success: true, message };
          this.toast.success(message);
        },
        error: (err) => {
          this.importingLeads = false;
          const message = this.apiErrorMessage(err, 'CRM sync failed');
          this.syncResult = { success: false, message };
          this.toast.error(message);
        },
      });
  }

  importCrmLeads(item: IntegrationItem, event?: Event): void {
    this.openSyncData(item, event);
  }

  copyWebhook(url: string): void {
    navigator.clipboard.writeText(url).then(() => {
      this.copiedWebhook = true;
      this.toast.success('Inbound Webhook URL copied to clipboard');
      setTimeout(() => (this.copiedWebhook = false), 2000);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
